import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Zap, Bot, Workflow, BarChart3, Shield, Users, Globe, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-700 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 dark:text-white">{process.env.REACT_APP_APP_NAME || 'Flowteras'}</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/product" className="text-emerald-700 font-medium">Product</Link>
            <Link to="/pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</Link>
            <Link to="/about" className="text-slate-600 hover:text-slate-900 transition-colors">About</Link>
            <Link to="/contact" className="text-slate-600 hover:text-slate-900 transition-colors">Contact</Link>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <Link to="/login"><Button variant="ghost">Log in</Button></Link>
            <Link to="/signup"><Button className="bg-emerald-700 hover:bg-emerald-800 rounded-full">Get Started</Button></Link>
          </div>
          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
    </nav>
  );
};

const ProductPage = () => {
  const features = [
    {
      icon: MessageSquare,
      title: "Unified Inbox",
      description: "All your WhatsApp conversations in one place. Filter by status, assign to team members, and never miss a message.",
      details: ["Real-time message sync", "Smart conversation threading", "Quick reply templates", "Message search & history"]
    },
    {
      icon: Bot,
      title: "AI Message Intelligence",
      description: "Our AI understands context, detects intent, and suggests the perfect response for every customer message.",
      details: ["Intent classification", "Sentiment analysis", "Language detection", "Smart reply suggestions"]
    },
    {
      icon: Workflow,
      title: "Workflow Automation",
      description: "Build powerful automations without code. Create triggers, conditions, and actions with our visual builder.",
      details: ["Visual drag-and-drop builder", "Custom triggers & conditions", "Multi-step workflows", "Approval flows"]
    },
    {
      icon: BarChart3,
      title: "Analytics & Insights",
      description: "Track conversation metrics, team performance, and customer satisfaction in real-time dashboards.",
      details: ["Response time tracking", "Team performance metrics", "Customer satisfaction scores", "Custom reports"]
    },
    {
      icon: Users,
      title: "Team Collaboration",
      description: "Assign conversations, leave internal notes, and collaborate seamlessly with your team.",
      details: ["Role-based access", "Conversation assignment", "Internal notes", "Team chat"]
    },
    {
      icon: Globe,
      title: "Multi-Language Support",
      description: "Serve customers in their preferred language with automatic translation and localized responses.",
      details: ["Auto language detection", "Built-in translation", "Localized templates", "Multi-language FAQ"]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-emerald-50 to-slate-100 dark:from-slate-950 dark:to-emerald-950">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 dark:text-white mb-6" data-testid="product-title">
            Built for Modern Business Communication
          </h1>
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-10">
            {process.env.REACT_APP_APP_NAME || 'Flowteras'} combines AI intelligence with human expertise to deliver exceptional customer experiences through WhatsApp.
          </p>
          <Link to="/signup">
            <Button className="btn-primary text-lg px-10 py-6" data-testid="product-cta">
              Start Free Trial
            </Button>
          </Link>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="marketing-card" data-testid={`product-feature-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-emerald-700 dark:text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">{feature.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.details.map((detail, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-semibold text-center text-slate-900 dark:text-white mb-16">
            How It Works
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Connect WhatsApp", desc: "Link your WhatsApp Business account in minutes" },
              { step: "2", title: "Train Your AI", desc: "Add FAQs and let AI learn your business" },
              { step: "3", title: "Build Workflows", desc: "Create automations for common scenarios" },
              { step: "4", title: "Delight Customers", desc: "Respond faster with AI assistance" }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-700 text-white text-xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-semibold text-slate-900 dark:text-white mb-6">
            Ready to Get Started?
          </h2>
          <Link to="/signup">
            <Button className="btn-primary text-lg px-10 py-6">
              Start Your Free Trial
            </Button>
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ProductPage;
